import {
  Component,
  OnDestroy,
  OnInit,
  Output,
  EventEmitter,
  ViewEncapsulation,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { Event } from '../../../../data/models/interfaces/event';
import { EventsService } from '../../../../data/services/events.service';
import { PageEvent } from '@angular/material/paginator';
import { ClassRoomsService } from './../../../../data/services/classroom.service';
import { OccasionParamsDto } from './../../../../data/models/dto/occasion-params.dto';
import { PageDto } from './../../../../data/models/dto/arrival/page.dto';
import { ClassroomFilterDto } from 'src/app/data/models/dto/classroom-filter.dto';
import { UserUtil } from 'src/app/data/utils/user.utils';
import { YearSelectComponent } from 'src/app/ui/components/year-select/year-select.component';
import { Month } from 'src/app/ui/components/month-filter/month.model';
import { RESTRICTED_ROUTE_PROFILES } from '../../../../data/utils/profile.utils';
import { MANAGEMENT_ADVISOR_PROFILES } from '../../../../data/utils/profile.utils';
@Component({
  selector: 'app-events-list',
  templateUrl: './events-list.component.html',
  styleUrls: ['./events-list.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class EventsListComponent implements OnInit, OnDestroy {
  @Output() bottomsheet = new EventEmitter<string>();
  @ViewChild('yearSelectId') yearSelect!: YearSelectComponent;

  profilesElegibleToManageEvents = RESTRICTED_ROUTE_PROFILES(
    MANAGEMENT_ADVISOR_PROFILES,
  );

  private subscription: Subscription = new Subscription();

  loading: boolean = true;

  events: Event[] = [];
  eventsSource: Event[] = [];

  //pagination
  params?: OccasionParamsDto = { page: 1, limit: 15 };
  page?: PageDto<Event>;

  //filters
  textSearch: string = '';
  years?: string[];
  selectedYear?: string;
  selectedMonth?: Month;
  startTxtYear = '';
  idSchool = this.schools[0].id;
  filterClassroom: ClassroomFilterDto = {
    year: this.startTxtYear,
    idSchool: this.idSchool,
  };
  selectedIdsClassrooms: string[] = [];
  disableResetFilters = true;

  private readonly selectedYearKey = 'eventsListSelectedYear';
  private readonly selectedIdsClassroomsKey = 'eventsListSelectedIdsClassrooms';
  private readonly selectedMonthKey = 'eventsListSelectedMonth';

  constructor(
    private readonly router: Router,
    private readonly service: EventsService,
    private readonly classroomsService: ClassRoomsService,
    private readonly userUtil: UserUtil,
  ) {}

  private get schools() {
    return this.userUtil.getSchools();
  }

  async ngOnInit() {
    this.loading = true;

    await this.getStoredData();
    await this.getYears();

    this.filterClassroom.year = this.selectedYear;

    await this.reloadEvents();
  }

  async getStoredData() {
    const yearStored = localStorage.getItem(this.selectedYearKey);
    if (yearStored) {
      this.selectedYear = yearStored;
    }

    const classroomsStored = localStorage.getItem(
      this.selectedIdsClassroomsKey,
    );
    if (classroomsStored) {
      this.selectedIdsClassrooms = classroomsStored.split(',');
    }

    const monthStored = localStorage.getItem(this.selectedMonthKey);
    if (monthStored) {
      try {
        this.selectedMonth = JSON.parse(monthStored) as Month;
      } catch (e) {
        console.error('Could not parse stored month', e);
        localStorage.removeItem(this.selectedMonthKey);
      }
    }
  }

  async getEvents(params?: OccasionParamsDto) {
    const eventsList = await this.service.getAll(params).toPromise();
    this.page = eventsList;
    this.events = eventsList.data;
    this.eventsSource = eventsList.data;
  }

  async getYears(): Promise<void> {
    try {
      const result = await this.classroomsService
        .getYears(this.filterClassroom)
        .toPromise();
      if (result && result.length > 0) {
        this.years = result;
        if (!this.selectedYear || !result.includes(this.selectedYear)) {
          this.selectedYear = result[0];
        }
        this.filterClassroom.year = this.selectedYear;
      }
    } catch (error) {
      console.log(error);
    }
  }

  async filter(value: string) {
    this.textSearch = value;
    await this.reloadEvents();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  handlePageEvent(event: PageEvent) {
    const params = this.buildParams(
      this.selectedYear,
      event.pageSize,
      event.pageIndex + 1,
      this.selectedMonth?.num,
      this.idSchool,
      this.selectedIdsClassrooms,
    );

    this.getEvents(params);
  }

  async changeMonth(month: Month) {
    this.selectedMonth = month;
    this.saveSelectedMonth(month);
    await this.reloadEvents();
  }

  async changeYear(year: string) {
    if (this.selectedYear && this.selectedYear !== year) {
      this.selectedIdsClassrooms = [];
      localStorage.removeItem(this.selectedIdsClassroomsKey);
    }
    this.selectedYear = year;
    this.saveSelectedYear(year);
    await this.reloadEvents();
    this.resetFiltersStatus();
  }

  private buildParams(
    year?: string,
    limit?: number,
    page?: number,
    month?: number,
    idSchool?: string,
    idsClassrooms?: string[],
    textSearch?: string,
  ): OccasionParamsDto {
    return {
      year: year,
      month: month,
      limit: limit,
      page: page,
      idSchool: idSchool,
      idsClassrooms: idsClassrooms,
      textSearch: textSearch,
    };
  }

  private async reloadEvents() {
    await this.getEvents(
      this.buildParams(
        this.selectedYear,
        this.params?.limit,
        this.params?.page,
        this.selectedMonth?.num,
        this.idSchool,
        this.selectedIdsClassrooms,
        this.textSearch,
      ),
    );

    this.loading = false;
  }

  async resetFilters() {
    if (
      this.selectedIdsClassrooms.length > 0 ||
      this.selectedYear != this.years![0]
    ) {
      this.selectedYear = this.years![0];
      this.resetClassroomFilter();
      this.yearSelect.reset();
      await this.reloadEvents();
    }
    this.disableResetFilters = true;
    this.selectedMonth = undefined;

    localStorage.removeItem(this.selectedYearKey);
    localStorage.removeItem(this.selectedIdsClassroomsKey);
    localStorage.removeItem(this.selectedMonthKey);
  }

  private resetClassroomFilter() {
    this.selectedIdsClassrooms = [];
  }

  async handleSelectClassroom(ids: string[]) {
    this.selectedIdsClassrooms = ids;
    this.saveSelectedClassroom();
    await this.reloadEvents();
    this.resetFiltersStatus();
  }

  resetFiltersStatus() {
    this.disableResetFilters =
      this.selectedIdsClassrooms.length == 0 &&
      this.selectedYear == this.years![0];
  }

  private saveSelectedYear(year: string) {
    localStorage.setItem(this.selectedYearKey, year);
  }

  private saveSelectedClassroom() {
    localStorage.setItem(
      this.selectedIdsClassroomsKey,
      this.selectedIdsClassrooms.join(','),
    );
  }

  private saveSelectedMonth(month: Month) {
    localStorage.setItem(this.selectedMonthKey, JSON.stringify(month));
  }
}
